import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { errorSideEffects, loadingSideEffects, RTKSideEffectSchema, successSideEffects } from "..";
import { Toastify } from "../../../components/Toaster";

export interface SecurityHeadersConfig {
  XFrameOptionsHeaderEnabled: boolean;
  XContentTypeOptionsEnabled: boolean;
  ReferrerPolicyHeader: string;
  ContentSecurityPolicyHeader: string;
  ContentSecurityPolicyReportOnlyHeader: boolean;
  ReportingEndpointsHeader: string;
}

export interface SaveSecurityHeadersPayload {
  sectionKeys: string[];
  fields: Partial<SecurityHeadersConfig>;
}

export interface SecurityHeadersState extends RTKSideEffectSchema {
  config: SecurityHeadersConfig;
  savingSections: string[];
  saveSuccessCount: number;
}

const emptySecurityHeadersConfig: SecurityHeadersConfig = {
  XFrameOptionsHeaderEnabled: false,
  XContentTypeOptionsEnabled: false,
  ReferrerPolicyHeader: "",
  ContentSecurityPolicyHeader: "",
  ContentSecurityPolicyReportOnlyHeader: false,
  ReportingEndpointsHeader: ""
};

const initialState: SecurityHeadersState = {
  isLoading: false,
  isSuccess: false,
  isError: false,
  error: "",
  config: emptySecurityHeadersConfig,
  savingSections: [],
  saveSuccessCount: 0
};

const securityHeadersSlice = createSlice({
  name: "securityHeaders",
  initialState,
  reducers: {
    // Fetch response security headers
    getSecurityHeadersRequest(state) {
      Object.assign(state, loadingSideEffects);
    },
    getSecurityHeadersSuccess(state, action: PayloadAction<SecurityHeadersConfig>) {
      Object.assign(state, successSideEffects);
      state.config = action.payload;
    },
    getSecurityHeadersError(state, action: PayloadAction<string>) {
      Object.assign(state, errorSideEffects);
      state.error = action.payload;
      Toastify("error", action.payload);
    },
    saveSecurityHeadersRequest(state, action: PayloadAction<SaveSecurityHeadersPayload>) {
      action.payload.sectionKeys.forEach((key) => {
        if (!state.savingSections.includes(key)) {
          state.savingSections.push(key);
        }
      });
    },
    saveSecurityHeadersSuccess(state, action: PayloadAction<{ sectionKeys: string[]; config: SecurityHeadersConfig }>) {
      state.savingSections = state.savingSections.filter((key) => !action.payload.sectionKeys.includes(key));
      state.config = action.payload.config;
      state.saveSuccessCount += 1;
      Toastify("success", "Security header settings saved successfully.");
    },
    saveSecurityHeadersError(state, action: PayloadAction<{ sectionKeys: string[]; error: string }>) {
      state.savingSections = state.savingSections.filter((key) => !action.payload.sectionKeys.includes(key));
      Toastify("error", action.payload.error);
    }
  }
});

export const { getSecurityHeadersRequest, getSecurityHeadersSuccess, getSecurityHeadersError, saveSecurityHeadersRequest, saveSecurityHeadersSuccess, saveSecurityHeadersError } =
  securityHeadersSlice.actions;

export default securityHeadersSlice.reducer;
